import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { sendChatMessage } from "@/lib/api";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I can answer questions about your market intelligence data. Try asking things like:\n- \"Top Reddit posts about laptops this week\"\n- \"New competitor pages for Harvey Norman\"\n- \"What are JB Hi-Fi's content gaps?\"" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}`);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text }]);
    setLoading(true);
    try {
      const resp = await sendChatMessage(text, sessionId);
      setMessages(prev => [...prev, { role: "assistant", text: resp.response, data: resp.data }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", text: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="chatbot-toggle"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        style={{ background: '#FFEE2F', color: '#1E191A' }}
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[420px] h-[560px] bg-white border border-gray-200 rounded-2xl flex flex-col shadow-2xl" data-testid="chatbot-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200" style={{ background: '#1E191A', borderRadius: '16px 16px 0 0' }}>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-sm font-bold text-white">Market Intelligence AI</span>
        </div>
        <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white" data-testid="chatbot-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${
              m.role === "user"
                ? "text-white"
                : "bg-gray-100 text-gray-800 border border-gray-200"
            }`} style={m.role === "user" ? { background: '#1E191A', color: '#FFFFFF' } : {}}>
              {m.text}
              {m.data && m.data.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                  {m.data.length} result{m.data.length !== 1 ? 's' : ''} found
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-xl px-3 py-2 border border-gray-200">
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="px-4 py-3 border-t border-gray-200">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Ask about your data..."
            className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-yellow-400"
            data-testid="chatbot-input"
          />
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            className="px-3 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
            style={{ background: '#FFEE2F', color: '#1E191A' }}
            data-testid="chatbot-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
